import React, { useEffect, useRef, useState } from 'react'
import { useCommissions } from './hooks'
import { FilterValue } from 'antd/es/table/interface'
import {
  Button,
  DatePicker,
  Input,
  InputRef,
  Space,
  Table,
  TimeRangePickerProps,
} from 'antd'
import { CCardBody } from '@coreui/react-pro'
import { ColumnsType } from 'antd/lib/table'
import { path } from '../../../../../libs'
import { Account, DataIndex, Commission } from './types'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
type Props = {
  strategyId: number
}
export const StrategyCommissions: React.FC<Props> = ({ strategyId }) => {
  const {
    commissions,
    total,
    totalCountOfCommissions,
    loading,
    onChangeCommissionsList,
    onChangeTotalList,
  } = useCommissions(strategyId)
  const [changed, setChanged] = useState(false)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('paidAt')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    if (changed) {
      onChangeCommissionsList({
        strategyId: strategyId,
        limit: pageSize,
        page: page,
        orderBy: orderBy,
        orderDirection: orderDirection,
        searchText: searchText,
      })
      onChangeTotalList({
        strategyId: strategyId,
        limit: pageSize,
        page: page,
        orderBy: orderBy,
        orderDirection: orderDirection,
        searchText: searchText,
      })
    } else {
      setChanged(true)
    }
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })
  // TODO - доработать фильтрацию дат после доработки бэка

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  const getColumnSearchDateProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      close,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'flex' }}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
          open={isCalendarOpen}
          onClick={() => setIsCalendarOpen(true)}
          allowEmpty={[true, true]}
          onCalendarChange={() => setIsCalendarOpen(false)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1]))
        : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })
  const columns: ColumnsType<Commission> = [
    {
      title: 'Investor',
      ...getColumnSearchProps('account.mtLogin'),
      render: (item: Commission) => (
        <a
          href={path.clients.details(String(item.investorClientId))}
          target="_blank"
        >
          {item.account.accountPlatform} {item.account.mtLogin}
        </a>
      ),
      sorter: true,
    },
    {
      title: 'Paid at',
      dataIndex: 'paidAt',
      ...getColumnSearchDateProps('paidAt'),
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Commission',
      dataIndex: 'traderCommissionUSD',
      key: 'traderCommissionUSD',
      render: (traderCommissionUSD: number) => traderCommissionUSD,
    },
  ]

  return (
    <CCardBody>
      <div className="mb-4">Total: {total} USD</div>
      <Table
        dataSource={commissions}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey={(record) => record.strategySubscriptionId + record.paidAt}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfCommissions,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      ></Table>
    </CCardBody>
  )
}
